import { useIsAdmin } from "@/hooks/useIsAdmin";
import { useSlotSpotlight, useAssignSlotSpotlight, SpotlightDisplay } from "@/hooks/useSlotSpotlight";
import { useSpotlightRate } from "@/hooks/useSpotlightRate";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useState, useRef, useEffect } from "react";
import { Sparkles, Check, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

interface SpotlightRateProps {
  /** Page identifier, e.g. "low-rate-car-loans" */
  pageSlug: string;
  /** Unique slot on this page, e.g. "secured-headline" */
  slotId: string;
  /** Fallback text if no spotlight is assigned or no data */
  fallback: string;
  /** Suffix to append, e.g. "% p.a." */
  suffix?: string;
  /** Number of decimal places */
  decimals?: number;
}

const DISPLAY_OPTIONS: { value: SpotlightDisplay; label: string }[] = [
  { value: "advertised", label: "Advertised Rate" },
  { value: "comparison", label: "Comparison Rate" },
  { value: "gap", label: "Gap (Comparison − Advertised)" },
];

function AllSpotlightsPicker({
  onPick,
  onClose,
  currentDisplay,
}: {
  onPick: (name: string, display: SpotlightDisplay) => void;
  onClose: () => void;
  currentDisplay: SpotlightDisplay;
}) {
  const [selectedDisplay, setSelectedDisplay] = useState<SpotlightDisplay>(currentDisplay);

  const { data: spotlights, isLoading } = useQuery({
    queryKey: ["all-spotlights-for-picker"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("rate_spotlights")
        .select("name, label")
        .order("name");
      if (error) throw error;
      return data;
    },
  });

  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  return (
    <div
      ref={ref}
      className="absolute left-0 z-[100] mt-1 w-80 max-w-[calc(100vw-2rem)] rounded-lg border bg-card shadow-xl animate-in fade-in slide-in-from-top-1"
    >
      {/* Display mode selector */}
      <div className="border-b px-3 py-2">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Display</span>
        <div className="flex gap-1 mt-1">
          {DISPLAY_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setSelectedDisplay(opt.value)}
              className={`rounded px-2 py-1 text-[10px] font-medium transition-colors ${
                selectedDisplay === opt.value
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-muted/80"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between border-b px-3 py-2">
        <span className="text-xs font-semibold text-foreground flex items-center gap-1">
          <Sparkles className="h-3 w-3 text-primary" /> Assign Spotlight
        </span>
        <button onClick={onClose} className="rounded p-0.5 hover:bg-muted">
          <X className="h-3.5 w-3.5 text-muted-foreground" />
        </button>
      </div>
      <div className="max-h-48 overflow-y-auto p-1">
        {isLoading && (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        )}
        {spotlights?.length === 0 && (
          <p className="px-3 py-2 text-xs text-muted-foreground">
            No spotlights created yet. Create one from the rate table first.
          </p>
        )}
        {spotlights?.map((s) => (
          <button
            key={s.name}
            onClick={() => onPick(s.name, selectedDisplay)}
            className="flex w-full items-start gap-2 rounded px-2.5 py-1.5 text-left text-xs hover:bg-muted transition-colors"
          >
            <div className="min-w-0">
              <div className="font-medium text-foreground truncate">{s.label || s.name}</div>
              <div className="font-mono text-[10px] text-muted-foreground truncate">{s.name}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Inline rate display that pulls from a spotlight slot.
 * - For visitors: renders the rate (or fallback).
 * - For admins: shows a clickable rate with dotted underline; clicking opens a picker to assign a spotlight + display mode.
 */
export function SpotlightRate({
  pageSlug,
  slotId,
  fallback,
  suffix = "",
  decimals = 2,
}: SpotlightRateProps) {
  const isAdmin = useIsAdmin();
  const { data: slotConfig, isLoading: slotLoading } = useSlotSpotlight(pageSlug, slotId);
  const spotlightName = slotConfig?.spotlightName ?? null;
  const display = slotConfig?.display ?? "advertised";
  const { data: rate, isLoading: rateLoading } = useSpotlightRate(spotlightName ?? "");
  const assign = useAssignSlotSpotlight();
  const [pickerOpen, setPickerOpen] = useState(false);

  // Compute the display value
  let displayValue = fallback;
  if (spotlightName && rate) {
    if (display === "advertised") {
      displayValue = `${rate.advertised_rate.toFixed(decimals)}${suffix}`;
    } else if (display === "comparison" && rate.comparison_rate != null) {
      displayValue = `${rate.comparison_rate.toFixed(decimals)}${suffix}`;
    } else if (display === "gap" && rate.comparison_rate != null) {
      displayValue = `${Math.abs(rate.comparison_rate - rate.advertised_rate).toFixed(decimals)}${suffix}`;
    }
  }

  const isLoading = slotLoading || (spotlightName && rateLoading);

  if (!isAdmin) {
    return <>{isLoading ? fallback : displayValue}</>;
  }

  // Admin view: clickable with visual indicator
  return (
    <span className="relative inline-block group">
      <button
        onClick={() => setPickerOpen(!pickerOpen)}
        className={`
          inline-flex items-center gap-1 rounded px-0.5 -mx-0.5
          ${spotlightName
            ? "border-b-2 border-dashed border-primary/40 hover:border-primary"
            : "border-b-2 border-dashed border-destructive/40 hover:border-destructive"
          }
          transition-colors cursor-pointer bg-transparent
        `}
        title={spotlightName ? `Spotlight: ${spotlightName} (${display})` : "No spotlight assigned — click to assign"}
      >
        {isLoading ? fallback : displayValue}
        <Sparkles className="h-3 w-3 text-primary/60 shrink-0" />
      </button>

      {pickerOpen && (
        <AllSpotlightsPicker
          currentDisplay={display}
          onClose={() => setPickerOpen(false)}
          onPick={(name, selectedDisplay) => {
            assign.mutate(
              { pageSlug, slotId, spotlightName: name, display: selectedDisplay },
              {
                onSuccess: () => {
                  toast.success(`Slot "${slotId}" → ${name} (${selectedDisplay})`);
                  setPickerOpen(false);
                },
                onError: (e) => toast.error(e.message),
              }
            );
          }}
        />
      )}

      {spotlightName && (
        <span className="hidden group-hover:block absolute left-0 top-full whitespace-nowrap text-[10px] font-mono text-muted-foreground mt-0.5 bg-card/90 backdrop-blur-sm rounded px-1 py-0.5 shadow-sm z-50">
          {spotlightName} ({display})
        </span>
      )}
    </span>
  );
}
